<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import NotificationItem from '../components/NotificationItem.vue'
import NotificationItemSkeleton from '../components/NotificationItemSkeleton.vue'
import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import Message from '@uiKit/elements/Message.vue'
import EmptyState from '@uiKit/components/EmptyState.vue'
import { routes } from '@app/router/routes'
import { useNotificationsQuery } from '../api/useNotificationsQuery'
import { useMarkNotificationReadMutation } from '../api/mutations/useMarkNotificationReadMutation'
import { useMarkAllNotificationsReadMutation } from '../api/mutations/useMarkAllNotificationsReadMutation'
import { useNotificationsStore } from '../store/notificationsStore'
import type { Notification, NotificationType } from '../types/notificationTypes'

const router = useRouter()
const store = useNotificationsStore()

const notificationsQuery = useNotificationsQuery()
const markReadMutation = useMarkNotificationReadMutation()
const markAllReadMutation = useMarkAllNotificationsReadMutation()

const notifications = computed(() => notificationsQuery.data.value ?? [])
const isLoading = computed(() => notificationsQuery.isLoading.value)
const hasError = computed(() => Boolean(notificationsQuery.error.value))
const unreadCount = computed(
  () => notifications.value.filter((notification) => !notification.isRead).length,
)

const ROUTE_BY_TYPE: Record<NotificationType, string | null> = {
  investment: routes.PORTFOLIO,
  closing_soon: routes.MARKETPLACE,
  rental_income: routes.WALLET,
  wallet: routes.WALLET,
  refund: routes.WALLET,
  kyc: routes.ONBOARDING_WIZARD,
  bank_account: routes.BANK_ACCOUNTS,
  property: routes.MARKETPLACE,
  tokens: routes.PORTFOLIO,
  failed: routes.WALLET,
  general: null,
}

const handleItemClick = (notification: Notification) => {
  if (!notification.isRead) markReadMutation.mutate(notification.id)
  store.closeDrawer()
  const route = ROUTE_BY_TYPE[notification.type]
  if (route) {
    router.push(route)
  }
}

const handleMarkAll = () => {
  markAllReadMutation.mutate()
}

const SKELETON_COUNT = 5
</script>

<template>
  <Box direction="col" class="notif-container">
    <Box v-if="unreadCount > 0" align="center" justify="end" class="notif-container__toolbar">
      <Button
        label="Mark all read"
        color="primary"
        variant="text"
        size="small"
        :loading="markAllReadMutation.isPending.value"
        @click="handleMarkAll"
      />
    </Box>

    <Box direction="col" class="notif-container__body">
      <Box v-if="isLoading" direction="col">
        <NotificationItemSkeleton v-for="i in SKELETON_COUNT" :key="i" />
      </Box>

      <Message
        v-else-if="hasError"
        color="danger"
        class="notif-container__error"
        message="Failed to load notifications. Please try again."
      />

      <Box v-else-if="notifications.length > 0" direction="col">
        <NotificationItem
          v-for="notification in notifications"
          :key="notification.id"
          :notification="notification"
          @click="handleItemClick"
        />
      </Box>

      <EmptyState
        v-else
        icon="bell"
        title="No notifications"
        description="You're all caught up! Check back later for updates on your investments."
      />
    </Box>
  </Box>
</template>

<style scoped>
.notif-container {
  flex: 1;
  min-height: 0;
  background: var(--p-surface-0);
}

.notif-container__toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 0.5rem 1.25rem;
  background: var(--p-surface-0);
  border-bottom: 1px solid var(--p-content-border-color);
}

.notif-container__body {
  flex: 1;
}

.notif-container__error {
  margin: 0.75rem 1.25rem;
}

</style>
